import type { ReactElement } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import { Tooltip } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import type { TopNWithCostDropdownItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { SelectionDropdownQuotaFooter } from "app/pages/sales-intelligence/common-components/table-actions-panel/components/SelectionDropdown/SelectionDropdownQuotaFooter/SelectionDropdownQuotaFooter";
import QuotaTag from "app/pages/sales-intelligence/modules/quota/components/QuotaTag/QuotaTag";
import QuotaTotalCost from "app/pages/sales-intelligence/modules/quota/components/QuotaTotalCost";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import RevealDropdownQuotaFooter from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/RevealDropdownQuotaFooter/RevealDropdownQuotaFooter";
import { selectIsContactsReadonly } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import BaseActivatedPanel from "../../../../table-actions-panel/components/BaseActivatedPanel/BaseActivatedPanel";
import type { BaseActivatedPanelProps } from "../../../../table-actions-panel/components/BaseActivatedPanel/types";
import SelectionDDItem from "../../../../table-actions-panel/components/SelectionDDItem/SelectionDDItem";
import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../../../../table-actions-panel/models/selection-dropdown-item/types";
import { createContactRevealByIdDto } from "../../../helpers/dto";
import revealSelectionDropdown from "../../../services/actions-panel/reveal-selection/revealSelectionDropdown";
import trackingService from "../../../services/tracking/trackingService";
import { tableRowsSelectionChangedAction, tableRowsSelectionToggledAction } from "../../../store/action-creators";
import {
    selectCheckedUniqueContactIds,
    selectContactsCost,
    selectMultipleRevealIsInProgress,
    selectRemainingQuota,
    selectTotalQuota,
} from "../../../store/selectors";
import type { ContactRevealByIdRequestDto } from "../../../types/dto";
import InnerLoader from "../InnerLoader/InnerLoader";

import {
    StyledEyeIcon,
    StyledLabel,
    StyledPanel,
    StyledRevealPriceBadgeWrapper,
    StyledSelectionDropdown,
    StyledSubmitButton,
} from "./styles";

type ActiveContactsRevealPanelProps = BaseActivatedPanelProps & {
    isLoading: boolean;
    notRevealedCount: number;
    contactsCount: number;
    areSearchResultsFetching: boolean;
    onSubmit(topNOrIds: number | readonly ContactRevealByIdRequestDto[], onDone: () => void): void;
};

const ActiveContactsRevealPanel = ({
    isLoading,
    notRevealedCount, // todo: remove notRevealedCount after LeadGenActiveContactsRevealPanelWrapper removing. And replace notRevealedCount to contactsCount in code below
    contactsCount,
    areSearchResultsFetching,
    onDeactivate,
    onSubmit,
}: ActiveContactsRevealPanelProps) => {
    const dispatch = useDispatch();
    const remainingRevealQuota = useSelector(selectRemainingQuota);
    const totalRevealQuota = useSelector(selectTotalQuota);
    const costPerRecord = useSelector(selectContactsCost);
    const selectedContactsIds = useSelector(selectCheckedUniqueContactIds);
    const isContactsReadonly = useSelector(selectIsContactsReadonly);
    const isMultipleRevealInProgress = useSelector(selectMultipleRevealIsInProgress);

    const isDataCreditsEnabled = Boolean(salesSettingsHelper.isDataCreditsEnabled);
    const isNoTouchUser = isNoTouch(swSettings);

    const dropdownItems = useMemo(() => {
        return revealSelectionDropdown.createItems(
            notRevealedCount,
            remainingRevealQuota,
            costPerRecord,
            isDataCreditsEnabled,
            isNoTouchUser,
        );
    }, [remainingRevealQuota, notRevealedCount, costPerRecord, isDataCreditsEnabled, isNoTouchUser]);

    const [activeSelectionItem, setActiveSelectionItem] = useState<SelectionDropdownItem>(
        revealSelectionDropdown.getInitiallySelectedItem(dropdownItems),
    );

    const totalCost = activeSelectionItem.value * costPerRecord;
    const isEnoughCredits = totalCost <= remainingRevealQuota;

    /**
     * React to selected rows change by updating the ManualSelectionItem
     */
    useEffect(() => {
        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            setActiveSelectionItem(new ManualSelectionItem(selectedContactsIds.length));
        }
    }, [selectedContactsIds.length]);
    /**
     * React to dropdown items change. Usually happens after successful reveal.
     * The activeSelectionItem should be updated accordingly.
     */
    useEffect(() => {
        const expectedSelectedItem = revealSelectionDropdown.getInitiallySelectedItem(dropdownItems);

        if (
            activeSelectionItem.id !== expectedSelectedItem.id ||
            activeSelectionItem.value !== expectedSelectedItem.value
        ) {
            setActiveSelectionItem(expectedSelectedItem);
        }
    }, [dropdownItems]);
    /**
     * React to activeSelectionItem by toggling the table rows selection column.
     */
    useLayoutEffect(() => {
        dispatch(tableRowsSelectionToggledAction(activeSelectionItem.id === ManualSelectionItem.ID));
    }, [activeSelectionItem]);

    function handleSubmit() {
        trackingService.trackRevealSubmitted(activeSelectionItem, remainingRevealQuota);

        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            dispatch(tableRowsSelectionChangedAction([]));
            dispatch(tableRowsSelectionToggledAction(false));
            onSubmit(createContactRevealByIdDto(selectedContactsIds), handleDeactivate);
        } else {
            onSubmit(activeSelectionItem.value, handleDeactivate);
        }
    }

    function handleDeactivate() {
        dispatch(tableRowsSelectionChangedAction([]));
        dispatch(tableRowsSelectionToggledAction(false));
        onDeactivate();
    }

    const prevRevealInProgressRef = useRef(isMultipleRevealInProgress);
    useEffect(() => {
        if (prevRevealInProgressRef.current && !isMultipleRevealInProgress) {
            handleDeactivate();
        }
        prevRevealInProgressRef.current = isMultipleRevealInProgress;
    }, [isMultipleRevealInProgress]);

    const renderRevealPriceBadge = (): ReactElement | null => {
        if (!isDataCreditsEnabled) {
            return null;
        }

        return (
            <Tooltip content={translate("si.table-actions-panel.cost.total.tooltip", { costPerRecord })}>
                <StyledRevealPriceBadgeWrapper>
                    <QuotaTotalCost
                        size="md"
                        isOutOfQuota={!isEnoughCredits}
                        totalCost={totalCost}
                    />
                </StyledRevealPriceBadgeWrapper>
            </Tooltip>
        );
    };

    const renderItemPriceBadge = (item: TopNWithCostDropdownItem): ReactElement | null => {
        if (!isDataCreditsEnabled) {
            return null;
        }

        const { key, replacements } = item.getCostTranslation();

        return (
            <QuotaTag
                type={item.isAvailable ? "light" : "warning"}
                size="md"
                title={translate(key, replacements)}
            />
        );
    };

    function renderContentOrLoader() {
        if (areSearchResultsFetching || isMultipleRevealInProgress) {
            return <InnerLoader text={translate("si.contacts-actions-panel.loading.text")} />;
        }

        return (
            <StyledPanel>
                <StyledEyeIcon>
                    <SWReactIcons
                        iconName="eye-icon-blue"
                        size="xs"
                    />
                </StyledEyeIcon>
                <StyledLabel data-automation="contacts-actions-panel.reveal-label">
                    {translate("si.advanced_search.contacts_search.action.reveal.label")}:
                </StyledLabel>
                <StyledSelectionDropdown
                    buttonText={activeSelectionItem.getSelectedTranslation()}
                    onSelect={setActiveSelectionItem}
                    isHideFooterDivider={isDataCreditsEnabled}
                    footer={
                        isDataCreditsEnabled ? (
                            <SelectionDropdownQuotaFooter
                                totalQuota={totalRevealQuota}
                                remainingQuota={remainingRevealQuota}
                                pricePerRecord={costPerRecord}
                                maxOptionValue={dropdownItems[dropdownItems.length - 1]?.value}
                                destinationType="reveal"
                            />
                        ) : (
                            <RevealDropdownQuotaFooter
                                isContactsReadonly={isContactsReadonly}
                                remainingRevealQuota={remainingRevealQuota}
                            />
                        )
                    }
                >
                    {dropdownItems
                        .map((item) => (
                            <SelectionDDItem
                                key={item.id}
                                item={item}
                                isSelected={activeSelectionItem.id === item.id}
                            >
                                {renderItemPriceBadge(item)}
                            </SelectionDDItem>
                        ))
                        .concat(
                            <SelectionDDItem
                                key={ManualSelectionItem.ID}
                                item={new ManualSelectionItem(selectedContactsIds.length)}
                                isSelected={activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </StyledSelectionDropdown>

                {renderRevealPriceBadge()}

                <StyledSubmitButton
                    remainingRevealQuota={remainingRevealQuota}
                    contactsTotalCount={contactsCount}
                    contactsToRevealCount={activeSelectionItem.value}
                    isNotEnoughCredits={!isEnoughCredits}
                    areSearchResultsFetching={areSearchResultsFetching}
                    isLoading={isLoading}
                    onClick={handleSubmit}
                />
            </StyledPanel>
        );
    }

    return (
        <BaseActivatedPanel
            onDeactivate={handleDeactivate}
            hideCloseButton={isLoading || isMultipleRevealInProgress}
        >
            {renderContentOrLoader()}
        </BaseActivatedPanel>
    );
};

export default ActiveContactsRevealPanel;
